import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { useToast } from '@/hooks/use-toast'
import {
  Loader2,
  Info,
  ShoppingCart,
  ShoppingBag,
  FileText,
  ClipboardList,
  Car,
} from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { ServerSideConversionsCard } from '@/components/integrations/ServerSideConversionsCard'

// Setting keys for conversion labels
const CONVERSION_LABELS = {
  PURCHASE: 'gads_label_purchase',
  PROVIDE_CONTACT_INFO: 'gads_label_provide_contact_info',
  SELECT_VEHICLE: 'gads_label_select_vehicle',
  GET_VEHICLE_QUOTES: 'gads_label_get_vehicle_quotes',
  GENERATE_LEAD: 'gads_label_generate_lead',
} as const

// Schema - all labels are optional strings
const conversionLabelsSchema = z.object({
  purchase: z.string().optional().or(z.literal('')),
  provide_contact_info: z.string().optional().or(z.literal('')),
  select_vehicle: z.string().optional().or(z.literal('')),
  get_vehicle_quotes: z.string().optional().or(z.literal('')),
  generate_lead: z.string().optional().or(z.literal('')),
})

type ConversionLabelsFormData = z.infer<typeof conversionLabelsSchema>

export function ConversionLabelsPage() {
  const { account } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const activeAccount = isImpersonating ? impersonatedAccount : account

  // Fetch account settings
  const { data: settings, isLoading } = useQuery({
    queryKey: ['account-settings', activeAccount?.id],
    queryFn: async () => {
      if (!activeAccount?.id) return {}

      const { data, error } = await supabase
        .from('account_settings')
        .select('*')
        .eq('account_id', activeAccount.id)

      if (error) throw error

      const settingsMap: Record<string, string> = {}
      data?.forEach((setting) => {
        settingsMap[setting.key] = setting.value || ''
      })
      return settingsMap
    },
    enabled: !!activeAccount?.id,
  })

  // Form
  const form = useForm<ConversionLabelsFormData>({
    resolver: zodResolver(conversionLabelsSchema),
    defaultValues: {
      purchase: '',
      provide_contact_info: '',
      select_vehicle: '',
      get_vehicle_quotes: '',
      generate_lead: '',
    },
  })

  // Update form when settings are loaded
  useEffect(() => {
    if (settings) {
      form.reset({
        purchase: settings[CONVERSION_LABELS.PURCHASE] || '',
        provide_contact_info: settings[CONVERSION_LABELS.PROVIDE_CONTACT_INFO] || '',
        select_vehicle: settings[CONVERSION_LABELS.SELECT_VEHICLE] || '',
        get_vehicle_quotes: settings[CONVERSION_LABELS.GET_VEHICLE_QUOTES] || '',
        generate_lead: settings[CONVERSION_LABELS.GENERATE_LEAD] || '',
      })
    }
  }, [settings, form])

  // Mutation
  const mutation = useMutation({
    mutationFn: async (data: ConversionLabelsFormData) => {
      if (!activeAccount?.id) throw new Error('No account found')

      const settingsToUpsert = [
        {
          account_id: activeAccount.id,
          key: CONVERSION_LABELS.PURCHASE,
          value: data.purchase || null,
          description: 'Google Ads conversion label for purchase events',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: CONVERSION_LABELS.PROVIDE_CONTACT_INFO,
          value: data.provide_contact_info || null,
          description: 'Google Ads conversion label for provide contact info events',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: CONVERSION_LABELS.SELECT_VEHICLE,
          value: data.select_vehicle || null,
          description: 'Google Ads conversion label for select vehicle events',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: CONVERSION_LABELS.GET_VEHICLE_QUOTES,
          value: data.get_vehicle_quotes || null,
          description: 'Google Ads conversion label for get vehicle quotes events',
          is_secret: false,
        },
        {
          account_id: activeAccount.id,
          key: CONVERSION_LABELS.GENERATE_LEAD,
          value: data.generate_lead || null,
          description: 'Google Ads conversion label for generate lead events',
          is_secret: false,
        },
      ]

      for (const setting of settingsToUpsert) {
        const { error } = await supabase
          .from('account_settings')
          .upsert(setting, { onConflict: 'account_id,key' })

        if (error) throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account-settings', activeAccount?.id] })
      toast({
        title: 'Conversion labels saved',
        description: 'Your Google Ads conversion labels have been updated.',
      })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-96" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-24" />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Server-side uploads first: it's the reliable path, and the labels below
          only matter for the browser-based tag. */}
      <ServerSideConversionsCard accountId={activeAccount?.id} />

      <Card>
        <CardHeader>
          <CardTitle>Conversion Labels</CardTitle>
          <CardDescription>
            Map DataLayer events to Google Ads conversion labels for precise tracking
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit((data) => mutation.mutate(data))}
              className="space-y-6"
            >
              {/* Info Alert */}
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  Each label connects a specific website action to a Google Ads conversion.
                  Find your labels in Google Ads &gt; Goals &gt; Conversions &gt; [Conversion Name] &gt;
                  Tag setup &gt; Use Google Tag Manager. Labels are alphanumeric strings like: abc123XYZ
                </AlertDescription>
              </Alert>

              {/* Booking Funnel Events Section */}
              <div className="border-t pt-6">
                <h3 className="mb-4 text-lg font-medium">Booking Funnel Events</h3>

                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="get_vehicle_quotes"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          Get Vehicle Quotes
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormDescription>
                          Fires when user submits the booking widget on homepage
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="select_vehicle"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          <Car className="h-4 w-4" />
                          Select Vehicle
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormDescription>
                          Fires when user selects a vehicle from the vehicle selection page
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="provide_contact_info"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          <ShoppingBag className="h-4 w-4" />
                          Provide Contact Info
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormDescription>
                          Fires when user clicks "Continue to Payment" after entering contact details
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="purchase"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          <ShoppingCart className="h-4 w-4" />
                          Purchase
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormDescription>
                          Fires when a booking is confirmed on the confirmation page
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Lead Generation Events Section */}
              <div className="border-t pt-6">
                <h3 className="mb-4 text-lg font-medium">Lead Generation Events</h3>

                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="generate_lead"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="flex items-center gap-2">
                          <ClipboardList className="h-4 w-4" />
                          Generate Lead
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormDescription>
                          Fires when user submits the contact form
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Save Changes
              </Button>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  )
}
